'use client';

import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useAgentSetupStatus } from '@/hooks/use-agent-setup-status';

const AGENT_SETUP_TOAST = 'agent-setup-toast';
const AGENT_SETUP_TOAST_DISMISS = 'agent-setup-toast-dismiss';

export function dismissAgentSetupToast(agentId: string) {
  toast.dismiss(agentId);
  window.dispatchEvent(new CustomEvent(AGENT_SETUP_TOAST_DISMISS, { detail: { agentId } }));
}

export function showAgentSetupToast(agentId: string) {
  dismissAgentSetupToast(agentId);
  toast.loading('Preparing...', { id: agentId, duration: Infinity });
  window.dispatchEvent(new CustomEvent(AGENT_SETUP_TOAST, { detail: { agentId } }));
}

function AgentSetupToastPoller({
  agentId,
  onComplete,
}: {
  agentId: string;
  onComplete: () => void;
}) {
  const { status, isComplete, isError, retry } = useAgentSetupStatus({
    agentId,
    enabled: true,
    pollIntervalMs: 3000,
  });

  useEffect(() => {
    if (!status) return;

    if (isComplete) {
      toast.success('Your agent is ready', { id: agentId, duration: Infinity });
      onComplete();
      return;
    }

    if (isError) {
      toast.error(status.setupError || 'Setup failed. Something went wrong.', {
        id: agentId,
        duration: Infinity,
        action: { label: 'Retry', onClick: () => retry() },
      });
      onComplete();
      return;
    }

    toast.loading(status.stepLabel, { id: agentId, duration: Infinity });
  }, [agentId, status, isComplete, isError, onComplete, retry]);

  return null;
}

export function AgentSetupToasts() {
  const [agentIds, setAgentIds] = useState<string[]>([]);

  useEffect(() => {
    const handleAdd = (e: CustomEvent<{ agentId: string }>) => {
      const { agentId } = e.detail;
      setAgentIds((prev) => (prev.includes(agentId) ? prev : [...prev, agentId]));
    };

    const handleDismiss = (e: CustomEvent<{ agentId: string }>) => {
      const { agentId } = e.detail;
      setAgentIds((prev) => prev.filter((id) => id !== agentId));
    };

    window.addEventListener(AGENT_SETUP_TOAST, handleAdd as EventListener);
    window.addEventListener(AGENT_SETUP_TOAST_DISMISS, handleDismiss as EventListener);

    return () => {
      window.removeEventListener(AGENT_SETUP_TOAST, handleAdd as EventListener);
      window.removeEventListener(AGENT_SETUP_TOAST_DISMISS, handleDismiss as EventListener);
    };
  }, []);

  return (
    <>
      {agentIds.map((agentId) => (
        <AgentSetupToastPoller
          key={agentId}
          agentId={agentId}
          onComplete={() => setAgentIds((prev) => prev.filter((id) => id !== agentId))}
        />
      ))}
    </>
  );
}
